import {
  Box,
  Button,
  Input,
  IconButton,
  useToast,
  SimpleGrid,
  Card,
  CardBody,
  Text,
  Badge,
  VStack,
  HStack,
  Heading,
  Divider,
  Flex,
  Spacer,
  Select,
  InputGroup,
  InputLeftElement,
  Collapse,
  Wrap
} from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import { MdSearch, MdExpandMore, MdExpandLess, MdClose, MdEdit, MdGavel, MdAssessment } from 'react-icons/md';
import { policiaMunicipalApi, MultaPolicia } from '../../services/policiaMunicipalApi';
import { useSidebar } from '../../hooks/useSidebar';
import EstadisticasMultas from './EstadisticasMultas';
import ModalCalificarMulta from './ModalCalificarMulta';
import ReportesMultasJuez from './ReportesMultasJuez';
import { ReporteService } from '../../services/reporteService';

function MultasJuez() {
  const [multas, setMultas] = useState<MultaPolicia[]>([]);
  const [loading, setLoading] = useState(true);
  const [mostrarCalificadas, setMostrarCalificadas] = useState(false);
  const [filtros, setFiltros] = useState({
    texto: '',
    fechaIndividual: '',
    fechaInicio: '',
    fechaFin: '',
    estatus: ''
  });
  const [paginaActual, setPaginaActual] = useState(1);
  const multasPorPagina = 50;
  const toast = useToast();
  const { mini } = useSidebar();
  const [modalCalificarOpen, setModalCalificarOpen] = useState(false);
  const [multaACalificar, setMultaACalificar] = useState<MultaPolicia | null>(null);
  const [modalReporteOpen, setModalReporteOpen] = useState(false);

  // Cargar multas
  const fetchMultas = async () => {
    try {
      setLoading(true);
      const data = await policiaMunicipalApi.getMultas();
      setMultas(data);
    } catch (error) {
      toast({
        title: 'Error al cargar infracciones',
        description: error instanceof Error ? error.message : 'Error desconocido',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMultas();
  }, []);

  // Filtrar multas
  const multasFiltradas = multas.filter(multa => {
    // Filtro por calificado/no calificado
    if (!mostrarCalificadas && multa.calificado) return false;
    if (mostrarCalificadas && !multa.calificado) return false;

    // Filtro por texto
    if (filtros.texto) {
      const texto = filtros.texto.toLowerCase();
      const coincide = 
        multa.folio?.toLowerCase().includes(texto) ||
        multa.nombreConductor?.toLowerCase().includes(texto) ||
        multa.apellidosConductor?.toLowerCase().includes(texto) ||
        multa.numeroPlaca?.toLowerCase().includes(texto) ||
        multa.nombreOficial?.toLowerCase().includes(texto);
      if (!coincide) return false;
    }

    // Filtro por fecha individual
    if (filtros.fechaIndividual) {
      const fechaMulta = multa.fechaInfraccion?.split('T')[0];
      if (fechaMulta !== filtros.fechaIndividual) return false;
    }

    // Filtro por rango de fechas
    if (filtros.fechaInicio && filtros.fechaFin) {
      const fechaMulta = multa.fechaInfraccion?.split('T')[0];
      if (!fechaMulta || fechaMulta < filtros.fechaInicio || fechaMulta > filtros.fechaFin) {
        return false;
      }
    }

    // Filtro por estatus
    if (filtros.estatus && multa.estatus !== filtros.estatus) return false;

    return true;
  });

  // Ordenar: no calificadas primero por fecha más antigua, calificadas por fecha más reciente
  const multasOrdenadas = [...multasFiltradas].sort((a, b) => {
    if (!mostrarCalificadas) {
      // Para no calificadas: más antiguas primero
      return new Date(a.fechaInfraccion || '').getTime() - new Date(b.fechaInfraccion || '').getTime();
    } else {
      // Para calificadas: más recientes primero
      return new Date(b.fechaInfraccion || '').getTime() - new Date(a.fechaInfraccion || '').getTime();
    }
  });

  // Paginación
  const totalPaginas = Math.ceil(multasOrdenadas.length / multasPorPagina);
  const indiceInicio = (paginaActual - 1) * multasPorPagina;
  const indiceFin = indiceInicio + multasPorPagina;
  const multasPaginadas = multasOrdenadas.slice(indiceInicio, indiceFin);

  // Limpiar filtros
  const limpiarFiltros = () => {
    setFiltros({
      texto: '',
      fechaIndividual: '',
      fechaInicio: '',
      fechaFin: '',
      estatus: ''
    });
    setPaginaActual(1);
  };

  // Verificar si hay filtros activos
  const hayFiltrosActivos = Object.values(filtros).some(v => v !== '');

  // Generar rango de fechas para reporte
  const rangoFechasReporte = ReporteService.formatearRangoFechas(
    filtros.fechaInicio,
    filtros.fechaFin,
    filtros.fechaIndividual
  );
  
  const handleGenerarReporte = () => {
    if (multasFiltradas.length === 0) {
      toast({
        title: 'Sin datos para reporte',
        description: 'No hay multas que coincidan con los filtros aplicados',
        status: 'warning',
        duration: 4000,
        isClosable: true
      });
      return;
    }
    
    setModalReporteOpen(true);
  };

  // Abrir modal de calificación
  const abrirModalCalificar = (multa: MultaPolicia) => {
    setMultaACalificar(multa);
    setModalCalificarOpen(true);
  };

  // Cerrar modal y recargar datos
  const cerrarModalCalificar = () => {
    setModalCalificarOpen(false);
    setMultaACalificar(null);
    fetchMultas();
  };

  const getEstatusColor = (estatus: string) => {
    switch (estatus?.toLowerCase()) {
      case 'pendiente': return '#5b182b';
      case 'pagado': return '#bb8c52';
      case 'cancelado': return '#691f30';
      default: return 'gray';
    }
  };

  if (loading) {
    return (
      <Box p={4} pt={50}>
        <Text>Cargando infracciones...</Text>
      </Box>
    );
  }

  return (
    <Box p={4} pt={50} width="100%" position="relative">
      {/* Estadísticas */}
      <EstadisticasMultas multas={multas} />

      {/* Filtros */}
      <Card mb={4} bg="#5b182b2a">
        <CardBody>
          <VStack spacing={4}>
            <Heading size="md" color="#5b182b" alignSelf="start">
              Filtros de Búsqueda
            </Heading>
            
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} w="full">
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <MdSearch color="gray.300" />
                </InputLeftElement>
                <Input
                  placeholder="Buscar por folio, conductor, placa u oficial"
                  value={filtros.texto}
                  onChange={e => setFiltros(prev => ({ ...prev, texto: e.target.value }))}
                  bg="white"
                />
              </InputGroup>
              
              <Select
                placeholder="Estatus"
                value={filtros.estatus}
                onChange={e => setFiltros(prev => ({ ...prev, estatus: e.target.value }))}
                bg="white"
              >
                <option value="PENDIENTE">Pendiente</option>
                <option value="PAGADO">Pagado</option>
                <option value="CANCELADO">Cancelado</option>
              </Select>
              
              <Input
                type="date"
                placeholder="Fecha individual"
                value={filtros.fechaIndividual}
                onChange={e => setFiltros(prev => ({ ...prev, fechaIndividual: e.target.value }))}
                bg="white"
              />
              
              <HStack>
                <Input
                  type="date"
                  placeholder="Fecha inicio"
                  value={filtros.fechaInicio}
                  onChange={e => setFiltros(prev => ({ ...prev, fechaInicio: e.target.value }))}
                  bg="white"
                  size="sm"
                />
                <Input
                  type="date"
                  placeholder="Fecha fin"
                  value={filtros.fechaFin}
                  onChange={e => setFiltros(prev => ({ ...prev, fechaFin: e.target.value }))}
                  bg="white"
                  size="sm"
                />
              </HStack>
            </SimpleGrid>
            
            <HStack spacing={3} justify="space-between" w="full">
              <HStack spacing={3}>
                <Button
                  color="#bb8c52"
                  borderColor="#bb8c52"
                  bgColor="#ffffff"
                  variant="outline"
                  size="sm"
                  onClick={limpiarFiltros}
                  isDisabled={!hayFiltrosActivos}
                >
                  Limpiar Filtros
                </Button>
                
                <Button
                  bg={mostrarCalificadas ? "#bb8c52" : "#5b182b"}
                  color="white"
                  _hover={{ bg: mostrarCalificadas ? "#a67b47" : "#691f30" }}
                  size="sm"
                  onClick={() => {
                    setMostrarCalificadas(!mostrarCalificadas);
                    setPaginaActual(1);
                  }}
                >
                  {mostrarCalificadas ? 'Ver Por Calificar' : 'Ver Calificadas'}
                </Button>
                
                <Collapse in={hayFiltrosActivos} animateOpacity>
                  <Badge size={"md"} bg="#b98b54" color="white" px={2} py={1} borderRadius="md">
                    {Object.values(filtros).filter(v => v !== '').length} filtro(s) activo(s)
                  </Badge>
                </Collapse>
                
                {hayFiltrosActivos && (
                  <Button
                    leftIcon={<MdAssessment />}
                    bg="#5b182b"
                    color="white"
                    _hover={{ bg: "#691f30" }}
                    size="sm"
                    onClick={handleGenerarReporte}
                  >
                    Generar Reporte
                  </Button>
                )}
              </HStack>      
            </HStack>
            
            <HStack w="full" justify="space-between" align="start">
              <VStack align="start" spacing={2} flex={1}>
                <Text fontSize="sm" color="gray.600">
                  Mostrando {(paginaActual - 1) * multasPorPagina + 1}-{Math.min(paginaActual * multasPorPagina, multasOrdenadas.length)} de {multasOrdenadas.length} infracciones
                </Text>
                
                <HStack spacing={3} align="center">
                  <Collapse in={hayFiltrosActivos} animateOpacity>
                    <Wrap spacing={2}>
                      {filtros.texto && (
                        <Badge
                          border="1px solid #bb8c52"
                          color="#bb8c52"
                          bg="transparent"
                          display="flex"
                          alignItems="center"
                          gap={1}
                          px={2}
                          py={1}
                          borderRadius="full"
                        >
                          Texto: "{filtros.texto}"
                          <IconButton
                            aria-label="Remover filtro texto"
                            icon={<MdClose />}
                            size="xs"
                            variant="ghost"
                            minW="auto"
                            h="auto"
                            p={0}
                            color="#bb8c52"
                            onClick={() => setFiltros(prev => ({ ...prev, texto: '' }))}
                          />
                        </Badge>
                      )}
                      
                      {filtros.estatus && (
                        <Badge
                          border="1px solid #bb8c52"
                          color="#bb8c52"
                          bg="transparent"
                          display="flex"
                          alignItems="center"
                          gap={1}
                          px={2}
                          py={1}
                          borderRadius="full"
                        >
                          Estatus: {filtros.estatus}
                          <IconButton
                            aria-label="Remover filtro estatus"
                            icon={<MdClose />}
                            size="xs"
                            variant="ghost"
                            minW="auto"
                            h="auto"
                            p={0}
                            color="#bb8c52"
                            onClick={() => setFiltros(prev => ({ ...prev, estatus: '' }))}
                          />
                        </Badge>
                      )}
                      
                      {filtros.fechaIndividual && (
                        <Badge
                          border="1px solid #bb8c52"
                          color="#bb8c52"
                          bg="transparent"
                          display="flex"
                          alignItems="center"
                          gap={1}
                          px={2}
                          py={1}
                          borderRadius="full"
                        >
                          Fecha: {new Date(filtros.fechaIndividual).toLocaleDateString('es-ES')}
                          <IconButton
                            aria-label="Remover filtro fecha"
                            icon={<MdClose />}
                            size="xs"
                            variant="ghost"
                            minW="auto"
                            h="auto"
                            p={0}
                            color="#bb8c52"
                            onClick={() => setFiltros(prev => ({ ...prev, fechaIndividual: '' }))}
                          />
                        </Badge>
                      )}
                      
                      {filtros.fechaInicio && (
                        <Badge
                          border="1px solid #bb8c52"
                          color="#bb8c52"
                          bg="transparent"
                          display="flex"
                          alignItems="center"
                          gap={1}
                          px={2}
                          py={1}
                          borderRadius="full"
                        >
                          Desde: {new Date(filtros.fechaInicio).toLocaleDateString('es-ES')}
                          <IconButton
                            aria-label="Remover filtro fecha inicio"
                            icon={<MdClose />}
                            size="xs"
                            variant="ghost"
                            minW="auto"
                            h="auto"
                            p={0}
                            color="#bb8c52"
                            onClick={() => setFiltros(prev => ({ ...prev, fechaInicio: '' }))}
                          />
                        </Badge>
                      )}
                      
                      {filtros.fechaFin && (
                        <Badge
                          border="1px solid #bb8c52"
                          color="#bb8c52"
                          bg="transparent"
                          display="flex"
                          alignItems="center"
                          gap={1}
                          px={2}
                          py={1}
                          borderRadius="full"
                        >
                          Hasta: {new Date(filtros.fechaFin).toLocaleDateString('es-ES')}
                          <IconButton
                            aria-label="Remover filtro fecha fin"
                            icon={<MdClose />}
                            size="xs"
                            variant="ghost"
                            minW="auto"
                            h="auto"
                            p={0}
                            color="#bb8c52"
                            onClick={() => setFiltros(prev => ({ ...prev, fechaFin: '' }))}
                          />
                        </Badge>
                      )}
                    </Wrap>
                  </Collapse>
                </HStack>
              </VStack>
              
              <HStack spacing={2}>
                <Badge bg="#bb8c52" color="white" variant="solid">
                  Página {paginaActual} de {totalPaginas}
                </Badge>
                <Badge bg="#5b182b" color="white" variant="solid">
                  Total: {multas.length}
                </Badge>
              </HStack>
            </HStack>
          </VStack>
        </CardBody>
      </Card>

      {/* Grid de multas */}
      {multasOrdenadas.length === 0 ? (
        <Card>
          <CardBody>
            <VStack spacing={4} py={8}>
              <Text fontSize="lg" color="gray.500">
                {mostrarCalificadas ? 'No hay infracciones calificadas' : 'No hay infracciones por calificar'}
              </Text>
              <Text fontSize="sm" color="gray.400">
                {multas.length === 0 
                  ? 'No hay infracciones registradas' 
                  : 'Intenta ajustar los filtros de búsqueda'
                }
              </Text>
            </VStack>
          </CardBody>
        </Card>
      ) : (
        <SimpleGrid 
          columns={{ base: 1, md: 2, lg: 3, xl: 4 }}
          spacing={4}
          w="100%"
          mx="auto"
        >
          {multasPaginadas.map(multa => (
            <Card 
              key={multa.id} 
              shadow="md" 
              borderRadius="lg" 
              _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }} 
              transition="all 0.2s"
              minW="300px"
              maxW="350px"
              h="auto"
              minH="400px"
              marginRight={2}
              border={!multa.calificado ? "2px solid #5b182b" : "1px solid #e2e8f0"}
            >
              <CardBody>
                <VStack align="stretch" spacing={3}>
                  {/* Header */}
                  <Flex align="center">
                    <VStack align="start" spacing={0}>
                      <Heading size="md" color="#5b182b">
                        {multa.folio}
                      </Heading>
                      <Text fontSize="sm" color="gray.500">
                        Infracción de Tránsito
                      </Text>
                    </VStack>
                    <Spacer />
                    <Badge 
                      bg={multa.calificado ? "#bb8c52" : "#5b182b"}
                      color="white"
                      variant="solid"
                      borderRadius="full"
                    >
                      {multa.calificado ? 'Calificada' : 'Por Calificar'}
                    </Badge>
                  </Flex>

                  <Divider />

                  {/* Información Principal */}
                  <VStack align="stretch" spacing={2}>
                    <SimpleGrid columns={2} spacing={3}>
                      <Box>
                        <Text fontWeight="semibold" fontSize="sm" color="gray.700">
                          Conductor
                        </Text>
                        <Text fontSize="sm">
                          {multa.ciudadano || 
                           (multa.nombreConductor && multa.apellidosConductor 
                            ? `${multa.nombreConductor} ${multa.apellidosConductor}`
                            : 'No registrado')
                          }
                        </Text>
                      </Box>
                      <Box>
                        <Text fontWeight="semibold" fontSize="sm" color="gray.700">
                          Fecha
                        </Text>
                        <Text fontSize="sm">
                          {multa.fechaInfraccion?.split('T')[0] || 'Sin fecha'}
                        </Text>
                      </Box>
                    </SimpleGrid>
                    
                    <SimpleGrid columns={2} spacing={3}>
                      <Box>
                        <Text fontWeight="semibold" fontSize="sm" color="gray.700">
                          Placa
                        </Text>
                        <Text fontSize="sm">{multa.numeroPlaca || 'N/A'}</Text>
                      </Box>
                      <Box>
                        <Text fontWeight="semibold" fontSize="sm" color="gray.700">
                          Vehículo
                        </Text>
                        <Text fontSize="sm">
                          {multa.marca && multa.modelo 
                            ? `${multa.marca} ${multa.modelo}`
                            : multa.tipoVehiculo || 'N/A'
                          }
                        </Text>
                      </Box>
                    </SimpleGrid>

                    <Box>
                      <Text fontWeight="semibold" fontSize="sm" color="gray.700">
                        Lugar de Infracción
                      </Text>
                      <Text fontSize="sm" noOfLines={2}>
                        {multa.lugarInfraccion || 'No especificado'}
                      </Text>
                    </Box>

                    <Box>
                      <Text fontWeight="semibold" fontSize="sm" color="gray.700">
                        Oficial
                      </Text>
                      <Text fontSize="sm">
                        {multa.nombreOficial || 'No registrado'}
                        {multa.gradoOficial && ` - ${multa.gradoOficial}`}
                      </Text>
                    </Box>
                  </VStack>

                  <Divider />

                  {/* Estados y Monto */}
                  <VStack align="stretch" spacing={2}>
                    <HStack justify="space-between">
                      <Text fontWeight="semibold" fontSize="sm" color="gray.700">
                        Estatus
                      </Text>
                      <Badge 
                        bg={getEstatusColor(multa.estatus || 'PENDIENTE')}
                        color="white"
                        variant="solid"
                        borderRadius="full"
                      >
                        {multa.estatus || 'PENDIENTE'}
                      </Badge>
                    </HStack>

                    <HStack justify="space-between">
                      <Text fontWeight="semibold" fontSize="sm" color="gray.700">
                        Monto
                      </Text>
                      <Text fontWeight="bold" color="#bb8c52">
                        ${Number(multa.monto ?? 0).toFixed(2)}
                      </Text>
                    </HStack>

                    {multa.articuloViolado && (
                      <Box>
                        <Text fontWeight="semibold" fontSize="sm" color="gray.700">
                          Artículo Violado
                        </Text>
                        <Text fontSize="sm">{multa.articuloViolado}</Text>
                      </Box>
                    )}
                  </VStack>

                  <Divider />

                  {/* Acciones */}
                  <HStack spacing={2} justify="center">
                    {!multa.calificado ? (
                      <Button
                        leftIcon={<MdGavel />}
                        size="sm"
                        bg="#5b182b"
                        color="white"
                        _hover={{ bg: "#691f30" }}
                        onClick={() => abrirModalCalificar(multa)}
                      >
                        Calificar
                      </Button>
                    ) : (
                      <Button
                        leftIcon={<MdEdit />}
                        size="sm"
                        color="#bb8c52"
                        borderColor="#bb8c52"
                        variant="outline"
                        _hover={{ bg: "#bb8c52", color: "white" }}
                      >
                        Editar
                      </Button>
                    )}
                  </HStack>
                </VStack>
              </CardBody>
            </Card>
          ))}
        </SimpleGrid>
      )}
      
      {/* Paginación */}
      {totalPaginas > 1 && (
        <VStack spacing={4} mt={6}>
          <HStack justify="center" spacing={2}>
            <Button
              size="sm"
              onClick={() => setPaginaActual(1)}
              isDisabled={paginaActual === 1}
              variant="outline"
              borderColor="#bb8c52"
              color="#bb8c52"
            >
              Primera
            </Button>
            <Button
              size="sm"
              onClick={() => setPaginaActual(paginaActual - 1)}
              isDisabled={paginaActual === 1}
              variant="outline"
              borderColor="#bb8c52"
              color="#bb8c52"
            >
              Anterior
            </Button>
            <Text fontSize="sm" px={4} py={2}>
              {paginaActual} de {totalPaginas}
            </Text>
            <Button
              size="sm"
              onClick={() => setPaginaActual(paginaActual + 1)}
              isDisabled={paginaActual === totalPaginas}
              variant="outline"
              borderColor="#bb8c52"
              color="#bb8c52"
            >
              Siguiente
            </Button>
            <Button
              size="sm"
              onClick={() => setPaginaActual(totalPaginas)}
              isDisabled={paginaActual === totalPaginas}
              variant="outline"
              borderColor="#bb8c52"
              color="#bb8c52"
            >
              Última
            </Button>
          </HStack>
        </VStack>
      )}
      
      {/* Modal de Calificación */}
      <ModalCalificarMulta
        isOpen={modalCalificarOpen}
        onClose={() => setModalCalificarOpen(false)}
        multa={multaACalificar}
        onCalificar={cerrarModalCalificar}
      />
      
      {/* Modal de reportes */}
      <ReportesMultasJuez
        isOpen={modalReporteOpen}
        onClose={() => setModalReporteOpen(false)}
        multas={multasFiltradas}
        filtros={filtros}
        rangoFechas={rangoFechasReporte}
      />
    </Box>
  );
}

export default MultasJuez;